import { useMemo } from "react";
import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import usePublicProductsService from "@app-contexts/public/features/products/data/services/usePublicProductsService";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import {
  ProductTypeSerializer,
  ProductVariantSerializer,
} from "@app-contexts/public/features/products/data/types/products";
import {
  PublicMembershipGroupSerializer,
  PublicMembershipLevelSerializer,
} from "@shared/modules/memberships/types";

export interface BasketDataMappings {
  eventsMapping: Record<number, PublicBaseEventSerializer>;
  ticketTypesMapping: Record<string, TicketTypeSerializer>;
  productsMapping: Record<number, ProductTypeSerializer>;
  productsAttributesMapping: Record<string, ProductVariantSerializer>;
  membershipLevelsMapping: Record<string, PublicMembershipLevelSerializer>;
}

export interface BasketDataQueries {
  eventQueries: {
    data?: {
      pages?: Array<{
        results: PublicBaseEventSerializer[];
      }>;
    };
    isLoading: boolean;
    isError: boolean;
  };
  productQueries: {
    data?: {
      pages?: Array<{
        results: ProductTypeSerializer[];
      }>;
    };
    isLoading: boolean;
    isError: boolean;
  };
  membershipQueries: {
    data?: {
      pages?: Array<{
        results: PublicMembershipGroupSerializer[];
      }>;
    };
    isLoading: boolean;
    isError: boolean;
  };
}

/**
 * Hook for fetching basket-related item data (events, products, memberships)
 * This service provides the raw data that can be used by multiple services
 * including basket upgrades, terms, tracking, etc.
 * TODO: use this with dependency injection to make it more reusable across different basket contexts
 */
export function usePublicBasketItemData(
  itemGroups: TicketItemGroupType[],
  enabled = false
) {
  const eventIds = useMemo(() => {
    if (!enabled || !itemGroups?.length) return [];
    return itemGroups
      .filter((item) => item.event)
      .map((item) => item.event)
      .filter((id, index, arr) => arr.indexOf(id) === index);
  }, [itemGroups, enabled]);

  const productIds = useMemo(() => {
    if (!enabled || !itemGroups?.length) return [];
    return itemGroups
      .filter((item) => item.product)
      .map((item) => item.product)
      .filter((id, index, arr) => arr.indexOf(id) === index);
  }, [itemGroups, enabled]);

  const membershipGroupIds = useMemo(() => {
    if (!enabled || !itemGroups?.length) return [];
    return itemGroups
      .filter((item) => item.membership_group)
      .map((item) => item.membership_group)
      .filter((id, index, arr) => arr.indexOf(id) === index);
  }, [itemGroups, enabled]);

  const { usePaginated: usePaginatedEvents } = usePublicEventService();
  const { usePaginated: usePaginatedProducts } = usePublicProductsService();
  const { usePaginated: usePaginatedMemberships } =
    usePublicMembershipGroupsService();

  // Queries - only run when enabled and IDs exist
  const eventQueries = usePaginatedEvents({
    params: {
      id__in: eventIds.join(","),
      // Request upgrade options for basket/cart upgrade functionality
      include_upgrade_options: true,
    },
    options: {
      enabled: enabled && eventIds.length > 0,
    },
  });

  const productQueries = usePaginatedProducts({
    params: {
      id__in: productIds.join(","),
    },
    options: {
      enabled: enabled && productIds.length > 0,
    },
  });

  const membershipQueries = usePaginatedMemberships({
    params: {
      id__in: membershipGroupIds.join(","),
    },
    options: {
      enabled: enabled && membershipGroupIds.length > 0,
    },
  });

  // Create mappings from query results
  const eventsMapping = useMemo(() => {
    const eventMap: Record<number, PublicBaseEventSerializer> = {};

    eventQueries?.data?.pages?.forEach((page) =>
      page.results.forEach((event) => {
        if (event) {
          eventMap[event.id] = event;
        }
      })
    );

    return eventMap;
  }, [eventQueries]);

  const ticketTypesMapping = useMemo(() => {
    const eventTicketTypeMap: Record<string, TicketTypeSerializer> = {};

    eventQueries?.data?.pages?.forEach((page) =>
      page.results.forEach((event) => {
        if (event) {
          event.ticket_types?.forEach((ticket_type) => {
            eventTicketTypeMap[`${ticket_type.event}:${ticket_type.id}`] =
              ticket_type;
          });
        }
      })
    );

    return eventTicketTypeMap;
  }, [eventQueries]);

  const productsMapping = useMemo(() => {
    const productMap: Record<number, ProductTypeSerializer> = {};

    productQueries?.data?.pages?.forEach((page) =>
      page.results.forEach((product) => {
        if (product) {
          productMap[product.id] = product;
        }
      })
    );

    return productMap;
  }, [productQueries]);

  const productsAttributesMapping = useMemo(() => {
    const productAttributeMap: Record<string, ProductVariantSerializer> = {};

    productQueries?.data?.pages?.forEach((page) =>
      page.results.forEach((product) => {
        if (product) {
          product.product_attributes?.forEach((product_attribute) => {
            productAttributeMap[`${product.id}:${product_attribute.id}`] =
              product_attribute;
          });
        }
      })
    );

    return productAttributeMap;
  }, [productQueries]);

  const membershipGroupsMapping = useMemo(() => {
    const membershipGroupMap: Record<number, PublicMembershipGroupSerializer> =
      {};

    membershipQueries?.data?.pages?.forEach((page) =>
      page.results.forEach((membership) => {
        if (membership) {
          membershipGroupMap[membership.id] = membership;
        }
      })
    );

    return membershipGroupMap;
  }, [membershipQueries]);

  const membershipLevelsMapping = useMemo(() => {
    const membershipLevelMap: Record<string, PublicMembershipLevelSerializer> =
      {};

    membershipQueries?.data?.pages?.forEach((page) =>
      page.results.forEach((membership) => {
        if (membership) {
          membership.membership_levels?.forEach((level) => {
            membershipLevelMap[`${membership.id}:${level.id}`] = level;
          });
        }
      })
    );

    return membershipLevelMap;
  }, [membershipQueries]);

  const isLoading =
    (enabled && eventIds.length > 0 && eventQueries.isLoading) ||
    (enabled && productIds.length > 0 && productQueries.isLoading) ||
    (enabled && membershipGroupIds.length > 0 && membershipQueries.isLoading);

  const isError =
    eventQueries.isError || productQueries.isError || membershipQueries.isError;

  // Raw query results for advanced usage
  const queries: BasketDataQueries = {
    eventQueries,
    productQueries,
    membershipQueries,
  };

  return {
    queries,
    eventsMapping,
    ticketTypesMapping,
    productsMapping,
    productsAttributesMapping,
    membershipGroupsMapping,
    membershipLevelsMapping,

    // Loading states
    isLoading,
    isError,
  };
}
